import React, { useState } from 'react';
import { invoiceTemplatesService } from '../../services/invoiceTemplatesService';
import SortIcon from './SortIcon';
import Pagination from './Pagination';
import { getKsefStatusBadge, getDateRange, sortData } from './ksefHelpers';
import ActionMenu from '../common/ActionMenu';

const SalesInvoiceList = ({
  invoices,
  ksefEnabled,
  ksefSending,
  canCorrectInvoices,
  onPreview,
  onCorrection,
  onSendToKsef,
  onDownloadUpo,
  setError
}) => {
  // Pagination
  const [invoicesPerPage, setInvoicesPerPage] = useState(25);
  const [invoicesCurrentPage, setInvoicesCurrentPage] = useState(1);

  // Sorting
  const [sortColumn, setSortColumn] = useState('data_wystawienia');
  const [sortDirection, setSortDirection] = useState('desc');

  // Filters
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterPayment, setFilterPayment] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Filtering
  const filteredInvoices = invoices.filter(invoice => {
    if (filterPeriod && filterPeriod !== 'all') {
      if (filterPeriod === 'custom') {
        if (filterDateFrom) {
          const fromDate = new Date(filterDateFrom);
          const invoiceDate = new Date(invoice.data_wystawienia);
          if (invoiceDate < fromDate) return false;
        }
        if (filterDateTo) {
          const toDate = new Date(filterDateTo);
          toDate.setHours(23, 59, 59, 999);
          const invoiceDate = new Date(invoice.data_wystawienia);
          if (invoiceDate > toDate) return false;
        }
      } else {
        const { from, to } = getDateRange(filterPeriod);
        if (from && to) {
          const invoiceDate = new Date(invoice.data_wystawienia);
          if (invoiceDate < from || invoiceDate > to) return false;
        }
      }
    }

    if (filterCustomer) {
      const customerMatch = (invoice.nabywca_nazwa || '').toLowerCase().includes(filterCustomer.toLowerCase()) ||
                           (invoice.nabywca_nip || '').includes(filterCustomer);
      if (!customerMatch) return false;
    }

    if (filterPayment && filterPayment !== 'all') {
      const paymentType = (invoice.forma_platnosci || '').toLowerCase();
      if (!paymentType.includes(filterPayment.toLowerCase())) return false;
    }

    if (filterStatus && filterStatus !== 'all') {
      const isPaid = invoice.status_platnosci === 'oplacona' || invoice.oplacona === true || invoice.oplacona === 1;
      if (filterStatus === 'oplacona' && !isPaid) return false;
      if (filterStatus === 'nieoplacona' && isPaid) return false;
    }

    if (filterSearch) {
      const searchLower = filterSearch.toLowerCase();
      const matchNumber = (invoice.numer_faktury || '').toLowerCase().includes(searchLower);
      const matchCustomer = (invoice.nabywca_nazwa || '').toLowerCase().includes(searchLower);
      if (!matchNumber && !matchCustomer) return false;
    }

    return true;
  });

  const resetFilters = () => {
    setFilterPeriod('all');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterCustomer('');
    setFilterPayment('');
    setFilterStatus('');
    setFilterSearch('');
  };

  const activeFiltersCount = [
    filterPeriod !== 'all' ? 1 : 0,
    filterCustomer ? 1 : 0,
    filterPayment && filterPayment !== 'all' ? 1 : 0,
    filterStatus && filterStatus !== 'all' ? 1 : 0,
    filterSearch ? 1 : 0
  ].reduce((a, b) => a + b, 0);

  const sortedInvoices = sortData(filteredInvoices, sortColumn, sortDirection);

  // Pagination
  const totalPages = Math.ceil(sortedInvoices.length / invoicesPerPage);
  const startIndex = (invoicesCurrentPage - 1) * invoicesPerPage;
  const paginatedInvoices = sortedInvoices.slice(startIndex, startIndex + invoicesPerPage);

  const handlePageChange = (page) => {
    setInvoicesCurrentPage(page);
  };

  const handleLimitChange = (newLimit) => {
    setInvoicesPerPage(newLimit);
    setInvoicesCurrentPage(1);
  };

  // PDF download
  const downloadInvoicePDF = async (invoiceId, invoiceNumber) => {
    try {
      const blob = await invoiceTemplatesService.getInvoicePdf(invoiceId);

      if (blob.type === 'application/pdf' || blob.size > 100) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `faktura_${invoiceNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        setError('Plik PDF nie zostal wygenerowany poprawnie. Sprawdz logi serwera.');
      }
    } catch (err) {
      setError('Blad pobierania PDF: ' + err.message);
    }
  };

  return (
    <div style={{
      backgroundColor: 'var(--card-bg)',
      borderRadius: '4px',
      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
      overflow: 'hidden'
    }}>
      <div style={{
        border: '1px solid var(--primary)', color: 'var(--primary)',
        padding: '0.5rem 0.75rem',
        fontSize: '12px',
        fontWeight: '600',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span>📄 Faktury wystawione ({filteredInvoices.length}{filteredInvoices.length !== invoices.length ? ` z ${invoices.length}` : ''})</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              padding: '4px 10px',
              fontSize: '11px',
              // Tokeny motywu: bialy tekst na polprzezroczystym tle byl niewidoczny w jasnym motywie
              backgroundColor: activeFiltersCount > 0 ? 'var(--warning)' : 'var(--card-bg)',
              border: activeFiltersCount > 0 ? '1px solid var(--warning)' : '1px solid var(--border)',
              color: activeFiltersCount > 0 ? '#000' : 'var(--text-secondary)'
            }}
          >
            🔍 Filtry {activeFiltersCount > 0 && `(${activeFiltersCount})`}
          </button>
          <span style={{ fontSize: '10px', opacity: 0.9 }}>
            Strona {invoicesCurrentPage} z {totalPages || 1}
          </span>
        </div>
      </div>

      {/* Panel filtrow */}
      {showFilters && (
        <InvoiceFilters
          filterSearch={filterSearch}
          setFilterSearch={setFilterSearch}
          filterPeriod={filterPeriod}
          setFilterPeriod={setFilterPeriod}
          filterDateFrom={filterDateFrom}
          setFilterDateFrom={setFilterDateFrom}
          filterDateTo={filterDateTo}
          setFilterDateTo={setFilterDateTo}
          filterCustomer={filterCustomer}
          setFilterCustomer={setFilterCustomer}
          filterPayment={filterPayment}
          setFilterPayment={setFilterPayment}
          filterStatus={filterStatus}
          setFilterStatus={setFilterStatus}
          activeFiltersCount={activeFiltersCount}
          onReset={resetFilters}
        />
      )}

      <div style={{ padding: '0.5rem' }}>
        {filteredInvoices.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)', fontSize: '12px', textAlign: 'center', padding: '1rem' }}>
            {invoices.length === 0 ? 'Brak faktur do wyswietlenia' : 'Brak faktur spelniajacych kryteria filtrowania'}
          </p>
        ) : (
          <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--bg)' }}>
                  <th style={{ padding: '0.4rem 0.5rem', textAlign: 'center', borderBottom: '1px solid var(--border)', fontWeight: '600', width: '40px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('id')}>LP<SortIcon column="id" sortColumn={sortColumn} sortDirection={sortDirection} /></th>
                  <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left', borderBottom: '1px solid var(--border)', fontWeight: '600', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('numer_faktury')}>Numer faktury<SortIcon column="numer_faktury" sortColumn={sortColumn} sortDirection={sortDirection} /></th>
                  <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left', borderBottom: '1px solid var(--border)', fontWeight: '600', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('data_wystawienia')}>Data wystawienia<SortIcon column="data_wystawienia" sortColumn={sortColumn} sortDirection={sortDirection} /></th>
                  <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left', borderBottom: '1px solid var(--border)', fontWeight: '600', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('nabywca_nazwa')}>Nabywca<SortIcon column="nabywca_nazwa" sortColumn={sortColumn} sortDirection={sortDirection} /></th>
                  <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right', borderBottom: '1px solid var(--border)', fontWeight: '600', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('suma_brutto')}>Suma brutto<SortIcon column="suma_brutto" sortColumn={sortColumn} sortDirection={sortDirection} /></th>
                  <th style={{ padding: '0.4rem 0.5rem', textAlign: 'center', borderBottom: '1px solid var(--border)', fontWeight: '600', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('status')}>Status<SortIcon column="status" sortColumn={sortColumn} sortDirection={sortDirection} /></th>
                  {ksefEnabled && (
                    <th style={{ padding: '0.4rem 0.5rem', textAlign: 'center', borderBottom: '1px solid var(--border)', fontWeight: '600', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('ksef_status')}>KSeF<SortIcon column="ksef_status" sortColumn={sortColumn} sortDirection={sortDirection} /></th>
                  )}
                  <th style={{ padding: '0.4rem 0.5rem', textAlign: 'center', borderBottom: '1px solid var(--border)', fontWeight: '600' }}>Akcje</th>
                </tr>
              </thead>
              <tbody>
                {paginatedInvoices.map((invoice, idx) => (
                  <tr key={`invoice-${invoice.id}-${idx}`} style={{ backgroundColor: idx % 2 === 0 ? 'var(--card-bg)' : 'var(--bg)' }}>
                    <td style={{ padding: '0.4rem 0.5rem', borderBottom: '1px solid var(--border)', textAlign: 'center', color: 'var(--text-secondary)' }}>
                      {startIndex + idx + 1}
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem', borderBottom: '1px solid var(--border)' }}>
                      <strong>{invoice.numer_faktury}</strong>
                      {invoice.typ_faktury === 'korekta' && (
                        <span style={{
                          marginLeft: '0.35rem',
                          padding: '0.15rem 0.35rem',
                          backgroundColor: 'var(--warning)',
                          color: '#000',
                          borderRadius: '3px',
                          fontSize: '9px'
                        }}>Korekta</span>
                      )}
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem', borderBottom: '1px solid var(--border)' }}>{invoice.data_wystawienia}</td>
                    <td style={{ padding: '0.4rem 0.5rem', borderBottom: '1px solid var(--border)' }}>
                      {invoice.nabywca_nazwa}
                      {invoice.nabywca_nip && (
                        <small style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '10px' }}>
                          NIP: {invoice.nabywca_nip}
                        </small>
                      )}
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem', borderBottom: '1px solid var(--border)', textAlign: 'right', fontWeight: '600' }}>
                      {parseFloat(invoice.suma_brutto).toFixed(2)} zl
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem', borderBottom: '1px solid var(--border)', textAlign: 'center' }}>
                      <span style={{
                        padding: '0.15rem 0.4rem',
                        borderRadius: '3px',
                        fontSize: '9px',
                        fontWeight: '500',
                        backgroundColor: invoice.status === 'wystawiona' ? 'var(--success)' : 'var(--text-muted)',
                        color: 'white'
                      }}>
                        {invoice.status}
                      </span>
                    </td>
                    {ksefEnabled && (
                      <td style={{ padding: '0.4rem 0.5rem', borderBottom: '1px solid var(--border)', textAlign: 'center' }}>
                        {getKsefStatusBadge(invoice) || (
                          <span style={{ color: 'var(--text-muted)', fontSize: '9px' }}>&mdash;</span>
                        )}
                      </td>
                    )}
                    <td style={{ padding: '0.4rem 0.5rem', borderBottom: '1px solid var(--border)', textAlign: 'center' }}>
                      <InvoiceActions
                        invoice={invoice}
                        ksefEnabled={ksefEnabled}
                        ksefSending={ksefSending}
                        canCorrectInvoices={canCorrectInvoices}
                        onPreview={onPreview}
                        onDownloadPDF={downloadInvoicePDF}
                        onCorrection={onCorrection}
                        onSendToKsef={onSendToKsef}
                        onDownloadUpo={onDownloadUpo}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={invoicesCurrentPage}
            totalPages={totalPages}
            startIndex={startIndex}
            perPage={invoicesPerPage}
            totalItems={filteredInvoices.length}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
            label={`faktur${filteredInvoices.length !== invoices.length ? ` (filtrowane z ${invoices.length})` : ''}`}
          />
          </>
        )}
      </div>
    </div>
  );
};

// --- Subcomponents ---

const InvoiceFilters = ({
  filterSearch, setFilterSearch,
  filterPeriod, setFilterPeriod,
  filterDateFrom, setFilterDateFrom,
  filterDateTo, setFilterDateTo,
  filterCustomer, setFilterCustomer,
  filterPayment, setFilterPayment,
  filterStatus, setFilterStatus,
  activeFiltersCount, onReset
}) => (
  <div style={{
    padding: '0.75rem',
    backgroundColor: 'var(--bg)',
    borderBottom: '1px solid var(--border)'
  }}>
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: '0.5rem',
      marginBottom: '0.5rem'
    }}>
      <div>
        <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '2px' }}>Szukaj</label>
        <input
          type="text"
          placeholder="Nr faktury lub klient..."
          value={filterSearch}
          onChange={(e) => setFilterSearch(e.target.value)}
          style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}
        />
      </div>

      <div>
        <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '2px' }}>Okres</label>
        <select
          value={filterPeriod}
          onChange={(e) => setFilterPeriod(e.target.value)}
          style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}
        >
          <option value="all">Wszystkie</option>
          <option value="today">Dzisiaj</option>
          <option value="week">Ostatnie 7 dni</option>
          <option value="month">Ten miesiac</option>
          <option value="year">Ten rok</option>
          <option value="custom">Zakres dat...</option>
        </select>
      </div>

      {filterPeriod === 'custom' && (
        <div>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '2px' }}>Od</label>
          <input
            type="date"
            value={filterDateFrom}
            onChange={(e) => setFilterDateFrom(e.target.value)}
            style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}
          />
        </div>
      )}

      {filterPeriod === 'custom' && (
        <div>
          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '2px' }}>Do</label>
          <input
            type="date"
            value={filterDateTo}
            onChange={(e) => setFilterDateTo(e.target.value)}
            style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}
          />
        </div>
      )}

      <div>
        <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '2px' }}>Klient</label>
        <input
          type="text"
          placeholder="Nazwa lub NIP..."
          value={filterCustomer}
          onChange={(e) => setFilterCustomer(e.target.value)}
          style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}
        />
      </div>

      <div>
        <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '2px' }}>Platnosc</label>
        <select
          value={filterPayment}
          onChange={(e) => setFilterPayment(e.target.value)}
          style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}
        >
          <option value="">Wszystkie</option>
          <option value="gotowka">Gotowka</option>
          <option value="karta">Karta</option>
          <option value="przelew">Przelew</option>
          <option value="blik">BLIK</option>
        </select>
      </div>

      <div>
        <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '2px' }}>Status</label>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}
        >
          <option value="">Wszystkie</option>
          <option value="oplacona">Oplacone</option>
          <option value="nieoplacona">Nieoplacone</option>
        </select>
      </div>
    </div>

    {activeFiltersCount > 0 && (
      <button
        onClick={onReset}
        style={{
          padding: '4px 10px',
          fontSize: '11px',
          border: '1px solid var(--danger)',
          backgroundColor: 'var(--card-bg)',
          color: 'var(--danger)'
        }}
      >
        {'✕'} Wyczysc filtry
      </button>
    )}
  </div>
);

const InvoiceActions = ({
  invoice,
  ksefEnabled,
  ksefSending,
  canCorrectInvoices,
  onPreview,
  onDownloadPDF,
  onCorrection,
  onSendToKsef,
  onDownloadUpo
}) => (
  <ActionMenu actions={[
    { icon: '👁️', label: 'Podgląd', color: 'var(--info)', onClick: () => onPreview(invoice.id) },
    { icon: '📄', label: 'Pobierz PDF', color: 'var(--success)', onClick: () => onDownloadPDF(invoice.id, invoice.numer_faktury) },
    invoice.typ_faktury !== 'korekta' && canCorrectInvoices && {
      icon: '🔄', label: 'Korekta', color: 'var(--warning)', onClick: () => onCorrection(invoice)
    },
    ksefEnabled && (!invoice.ksef_status || invoice.ksef_status === 'rejected' || invoice.ksef_status === 'error') && {
      icon: '📤',
      label: (invoice.ksef_status === 'rejected' || invoice.ksef_status === 'error') ? 'Wyślij ponownie KSeF' : 'Wyślij do KSeF',
      color: 'var(--purple)',
      disabled: ksefSending[invoice.id],
      onClick: () => onSendToKsef(invoice.id)
    },
    ksefEnabled && invoice.ksef_status === 'accepted' && {
      icon: '📋', label: 'Pobierz UPO', color: 'var(--info)', onClick: () => onDownloadUpo(invoice.id, invoice.ksef_numer)
    },
  ]} />
);

export default SalesInvoiceList;
